import { useRef, useState } from 'react';
import { CornerDownLeft } from 'lucide-react';

import { cn } from '@/lib/cn';
import { toast } from '@/lib/toast';
import { useCreateTask } from './useTasks';
import type { Task } from './types';

interface Props {
  parentId: number;
  /** Focus on mount: for the composer that opens from a row's + button. */
  autoFocus?: boolean;
  /** Escape, or blurring an empty field. Absent means the composer is permanent. */
  onCancel?: () => void;
  onCreated?: (task: Task) => void;
  placeholder?: string;
}

/**
 * One line, one subtask.
 *
 * Client and project are inherited from the parent server-side, so there is
 * nothing to pick here: a subtask that needed a client picker would be a task,
 * and would belong in Quick Add instead. Enter files it and leaves the field
 * open, because subtasks arrive in threes.
 */
export function SubtaskComposer({
  parentId,
  autoFocus = false,
  onCancel,
  onCreated,
  placeholder = 'Add a subtask',
}: Props) {
  const createTask = useCreateTask();
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed || createTask.isPending) return;

    createTask.mutate(
      { title: trimmed, parent_task_id: parentId },
      {
        onSuccess: (task) => {
          setTitle('');
          inputRef.current?.focus();
          toast.undoable(`Added "${task.title}"`, () => createTask.undo(task.id));
          onCreated?.(task);
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape' && onCancel) {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        // Closing on an empty blur keeps a row's composer from lingering, but
        // never discards typing.
        onBlur={() => {
          if (onCancel && !title.trim()) onCancel();
        }}
        aria-label="Subtask title"
        placeholder={placeholder}
        className={cn(
          'min-h-control min-w-0 flex-1 rounded border border-border bg-surface-1 px-2',
          'text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary',
          'focus:border-accent-border',
        )}
      />
      <button
        type="button"
        // onMouseDown, not onClick: the input's blur handler would otherwise
        // close a row composer out from under the click.
        onMouseDown={(e) => e.preventDefault()}
        onClick={submit}
        disabled={!title.trim() || createTask.isPending}
        aria-label="Add subtask"
        className={cn(
          'pressable flex size-control shrink-0 items-center justify-center rounded',
          'text-text-tertiary transition-colors duration-press',
          'hover:bg-surface-2 hover:text-text',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
        )}
      >
        <CornerDownLeft size={13} strokeWidth={1.5} aria-hidden />
      </button>
    </div>
  );
}
